export type CropShape = 'rect' | 'round';

export interface ImageCropSpec {
  aspect: number;
  title: string;
  helpText?: string;
  cropShape?: CropShape;
  outputMaxDimension: number;
  outputQuality: number;
  maxSizeBytes?: number;
  recommendedWidth: number;
  recommendedHeight: number;
}

export interface PixelCropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Kunne ikke laste bildet for beskjæring.'));
    img.src = src;
  });
}

function dataUrlToFile(dataUrl: string, fileName: string): File {
  const parts = dataUrl.split(',');
  const mimeMatch = parts[0]?.match(/:(.*?);/);
  const mime = mimeMatch?.[1] || 'image/jpeg';
  const binaryString = atob(parts[1] || '');
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mime });
}

export async function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Kunne ikke lese bildefilen.'));
    reader.readAsDataURL(file);
  });
}

export async function cropImageToFile(
  sourceDataUrl: string,
  croppedAreaPixels: PixelCropArea,
  originalFileName: string,
  spec: ImageCropSpec
): Promise<File> {
  const image = await loadImage(sourceDataUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Kunne ikke initialisere bildebehandling.');
  }

  const cropWidth = Math.max(1, Math.round(croppedAreaPixels.width));
  const cropHeight = Math.max(1, Math.round(croppedAreaPixels.height));
  const scale = Math.min(1, spec.outputMaxDimension / Math.max(cropWidth, cropHeight));
  const outputWidth = Math.max(1, Math.round(cropWidth * scale));
  const outputHeight = Math.max(1, Math.round(cropHeight * scale));

  canvas.width = outputWidth;
  canvas.height = outputHeight;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    cropWidth,
    cropHeight,
    0,
    0,
    outputWidth,
    outputHeight
  );

  const dataUrl = canvas.toDataURL('image/jpeg', spec.outputQuality);
  const safeName = originalFileName.replace(/\.[^.]+$/, '') || 'image';
  return dataUrlToFile(dataUrl, `${safeName}-cropped.jpg`);
}

export const AVATAR_IMAGE_SPEC: ImageCropSpec = {
  aspect: 1,
  title: 'Beskjær profilbilde',
  helpText: 'Anbefalt 1000x1000 (1:1).',
  cropShape: 'round',
  outputMaxDimension: 1200,
  outputQuality: 0.9,
  maxSizeBytes: 2.5 * 1024 * 1024,
  recommendedWidth: 1000,
  recommendedHeight: 1000,
};

export const CONTENT_IMAGE_SPEC_4_3: ImageCropSpec = {
  aspect: 4 / 3,
  title: 'Beskjær bilde',
  helpText: 'Anbefalt 1600x1200 (4:3).',
  cropShape: 'rect',
  outputMaxDimension: 1920,
  outputQuality: 0.88,
  maxSizeBytes: 2.5 * 1024 * 1024,
  recommendedWidth: 1600,
  recommendedHeight: 1200,
};

export const PROJECT_COVER_SPEC_4_3: ImageCropSpec = {
  ...CONTENT_IMAGE_SPEC_4_3,
  title: 'Beskjær cover-bilde',
};
